import { useState, useEffect, useRef } from 'react';
import {
  ArrowRight,
  ArrowDown,
  Leaf,
  X,
  Lock,
  Mail,
  MapPin,
  Sprout,
  ShoppingBag,
  Check,
  Compass,
  UserRound,
} from 'lucide-react';
import { signIn, signUp, recordFaunamasterClick } from '../lib/cloud';
import { requestPasswordReset } from '../lib/account';
import ProductTour from './ProductTour';
import CookieBanner from './CookieBanner';
import LegalTermsModal, { type LegalDocType } from './modals/LegalTermsModal';
import { useDialogA11y } from '../lib/useDialogA11y';

interface Props {
  onLoginSuccess: () => void;
  onStartTour?: () => void;
  onStartGuest?: () => void;
  focusStartTour?: boolean;
  initialShowLogin?: boolean;
}

const aerial = '/aerial-hero.jpg';

const workflow = [
  {
    title: 'Find din jord.',
    text: 'Find dit revir på luftfotoet og tegn dine marker. Plotwise holder styr på arealet.',
    label: 'Kort & marker',
    Icon: MapPin,
  },
  {
    title: 'Giv hver mark en plan.',
    text: 'Vælg frøblandinger til dine vildtagre og saml plejen i én plan, sæson for sæson.',
    label: 'Frø & pleje',
    Icon: Sprout,
  },
  {
    title: 'Se hvad du skal bruge.',
    text: 'Få det beregnede frøbehov og pakkeantal samlet i en indkøbsliste til Faunamaster.',
    label: 'Indkøbsliste',
    Icon: ShoppingBag,
  },
];
const exampleFields = ['Engstykket', 'Skovkanten', 'Bakkemarken'];

export default function LandingPage({
  onLoginSuccess,
  onStartTour,
  onStartGuest,
  focusStartTour,
  initialShowLogin = false,
}: Props) {
  const [showLogin, setShowLogin] = useState(initialShowLogin);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [showTour, setShowTour] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [exampleField, setExampleField] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocType>('terms');
  const [acceptedTermsSignup, setAcceptedTermsSignup] = useState(true);
  const modal = useRef<HTMLDivElement>(null);
  const tourButton = useRef<HTMLButtonElement>(null);
  const returningFromTour = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash || '';
    if (hash.includes('error=')) {
      const params = new URLSearchParams(hash.replace(/^#/, ''));
      const errCode = params.get('error_code');
      const errDesc = params.get('error_description');
      if (errCode === 'otp_expired' || hash.includes('expired')) {
        setError(
          'Dit invitations- eller loginlink er desværre udløbet eller allerede brugt. Indtast din e-mail herunder og klik "Glemt adgangskode" for at få et nyt direkte link tilsendt med det samme.',
        );
        setShowLogin(true);
        setAuthMode('login');
      } else if (errDesc || errCode) {
        setError(
          'Der opstod en fejl med loginlinket. Indtast din e-mail herunder for at modtage et nyt link.',
        );
        setShowLogin(true);
        setAuthMode('login');
      }
    } else {
      // Hurtig QR- eller kampagnelink direkte til oprettelse eller login
      const searchParams = new URLSearchParams(window.location.search);
      const isSignupParam =
        searchParams.get('signup') === '1' ||
        searchParams.get('signup') === 'true' ||
        searchParams.get('opret') === '1' ||
        searchParams.get('opret') === 'true';
      const isLoginParam =
        searchParams.get('login') === '1' || searchParams.get('login') === 'true';
      const isSignupHash = hash.includes('signup') || hash.includes('opret');
      const isLoginHash = hash.includes('login');

      if (isSignupParam || isSignupHash) {
        setShowLogin(true);
        setAuthMode('signup');
      } else if (isLoginParam || isLoginHash) {
        setShowLogin(true);
        setAuthMode('login');
      }
    }
  }, []);

  useEffect(() => {
    if (showTour) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.02, rootMargin: '0px 0px -20px 0px' },
    );
    document.querySelectorAll('.landing-reveal').forEach((element) => {
      element.classList.add('is-observed');
      observer.observe(element);
    });
    return () => observer.disconnect();
  }, [showTour]);

  useEffect(() => {
    if (focusStartTour) {
      const timer = setTimeout(() => {
        tourButton.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [focusStartTour]);

  useEffect(() => {
    if (!showTour && returningFromTour.current) {
      returningFromTour.current = false;
      tourButton.current?.focus();
    }
  }, [showTour]);

  useDialogA11y({ open: showLogin, onClose: () => setShowLogin(false), ref: modal });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setBusy(true);
    try {
      if (authMode === 'signup') {
        const result = await signUp(email.trim(), password, name);
        if (result.needsConfirmation) {
          setInfoMessage('Tjek din indbakke for at bekræfte din e-mailadresse, før du logger ind.');
        } else {
          onLoginSuccess();
        }
      } else {
        await signIn(email.trim(), password);
        onLoginSuccess();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (
        authMode === 'signup' &&
        (msg.toLowerCase().includes('already registered') ||
          msg.toLowerCase().includes('findes allerede') ||
          msg.toLowerCase().includes('already exists'))
      ) {
        setError(
          'Der findes allerede en bruger eller invitation til denne e-mail. Skift til "Log ind" ovenfor, eller klik "Glemt adgangskode" for at modtage et direkte login-link.',
        );
      } else {
        setError(
          msg ||
            (authMode === 'signup'
              ? 'Profilen kunne ikke oprettes.'
              : 'Ugyldig e-mail eller adgangskode.'),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  function handleStartTour() {
    if (onStartTour) {
      onStartTour();
      return;
    }
    setShowTour(true);
  }

  function exitTour() {
    returningFromTour.current = true;
    setShowTour(false);
  }

  function loginFromTour() {
    returningFromTour.current = false;
    setShowTour(false);
    setShowLogin(true);
  }

  const PreviewIcon = workflow[activeStep].Icon;
  if (showTour) return <ProductTour onExit={exitTour} onLogin={loginFromTour} />;

  return (
    <main className="landing-page">
      <section className="landing-hero" aria-label="Velkommen til Plotwise">
        <img className="landing-aerial" src={aerial} alt="" fetchPriority="high" />
        <header className="landing-nav">
          <a href="/" className="landing-wordmark" aria-label="Plotwise forside">
            <Leaf size={25} /> plotwise<span>.</span>
          </a>
          <nav aria-label="Forsidens navigation">
            <a className="landing-nav-link" href="#saadan">
              Sådan fungerer det
            </a>
            <button
              className="landing-login"
              style={{ minHeight: 44 }}
              onClick={() => setShowLogin(true)}
            >
              Log ind <ArrowRight size={17} />
            </button>
          </nav>
        </header>
        <div className="landing-intro">
          <p className="landing-eyebrow">
            <span /> TÆTTERE PÅ DIN JORD
          </p>
          <h1>
            plotwise<span>.</span>
          </h1>
          <h2>
            Mere liv i dit revir.
            <br />
            Mere ro i din plan.
          </h2>
          <p className="landing-description">
            Et godt revir starter med overblik. Saml dine marker, frøblandinger og plejeplaner — og
            giv dine idéer plads til at gro.
          </p>
          <div className="landing-actions">
            <button
              type="button"
              className="button primary landing-btn-primary"
              onClick={() => {
                if (onStartGuest) {
                  onStartGuest();
                } else if (onStartTour) {
                  onStartTour();
                }
              }}
              style={{ minHeight: 44 }}
            >
              <Sprout size={18} color="#86efac" />
              <span>Tegn din vildtager gratis</span>
              <ArrowRight size={18} />
            </button>
            <button
              type="button"
              className="landing-start landing-btn-secondary"
              onClick={() => {
                setAuthMode('signup');
                setShowLogin(true);
              }}
              style={{ minHeight: 44 }}
            >
              <UserRound size={17} />
              <span>Opret gratis profil</span>
            </button>
            <button
              ref={tourButton}
              type="button"
              className="landing-tour-subtle"
              onClick={handleStartTour}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.85rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                minHeight: 44,
                padding: '0 8px',
              }}
            >
              <Compass size={16} />
              <span>Start rundtur</span>
            </button>
            <a className="landing-learn" href="#saadan">
              <span>Lær Plotwise at kende</span>
              <ArrowDown size={17} />
            </a>
          </div>
          <p className="landing-small">
            <Check size={14} /> Intet krav om login · Beregn frøblandinger og vildtagre med det
            samme
          </p>
        </div>
        <div className="landing-landscape" aria-label="Klik på markerne i eksempelreviret">
          <svg viewBox="0 0 700 560" className="landing-field-lines" aria-hidden="true">
            <path
              className={exampleField === 0 ? 'is-selected' : ''}
              d="M95 170 255 105 335 222 269 318 113 291Z"
            />
            <path
              className={exampleField === 1 ? 'is-selected' : ''}
              d="M372 90 545 126 567 255 438 277 354 198Z"
            />
            <path
              className={exampleField === 2 ? 'is-selected' : ''}
              d="M307 357 452 301 588 355 551 470 367 485Z"
            />
          </svg>
          {exampleFields.map((name, index) => (
            <button
              key={name}
              className={`landing-field-marker landing-field-marker--${index} ${exampleField === index ? 'is-selected' : ''}`}
              aria-pressed={exampleField === index}
              onClick={() => setExampleField(index)}
            >
              <span />
              <span>{name}</span>
            </button>
          ))}
          <div className="landing-field-note" aria-live="polite">
            <span>EKSEMPELREVIR · KLIK PÅ EN MARK</span>
            <strong>{exampleFields[exampleField]}</strong>
            <p>Din jord. Dine muligheder.</p>
          </div>
        </div>
        <div className="landing-hero-bottom">
          <a href="#saadan">
            <ArrowDown size={16} /> Fra overblik til vildtager
          </a>
          <span>Luftfoto: Esri, Maxar, Earthstar Geographics · Illustrerede marker</span>
        </div>
      </section>

      {/* Login Modal */}
      {showLogin && (
        <div
          className="modal-backdrop"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 'var(--z-modal)',
            padding: '20px',
            backdropFilter: 'blur(4px)',
            overflowY: 'auto',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowLogin(false);
          }}
        >
          <div
            ref={modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-heading"
            className="panel landing-auth-panel"
            style={{
              width: '100%',
              maxWidth: '440px',
              margin: 'auto',
              backgroundColor: '#1c2217',
              border: '1px solid #36442c',
              borderRadius: '12px',
              padding: '32px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setShowLogin(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'none',
                border: 'none',
                color: '#9ba88d',
                cursor: 'pointer',
                minWidth: 44,
                minHeight: 44,
                display: 'grid',
                placeItems: 'center',
              }}
              aria-label="Luk"
            >
              <X size={20} />
            </button>

            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  color: '#bcd59f',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                <Leaf size={22} /> plotwise.
              </div>
              <h2
                id="login-heading"
                style={{ fontSize: '1.4rem', margin: '4px 0 6px', color: '#f1f4dc' }}
              >
                {authMode === 'signup' ? 'Opret profil' : 'Log ind'}
              </h2>
              <p style={{ color: '#9ba88d', fontSize: '0.88rem', margin: 0 }}>
                {authMode === 'signup'
                  ? 'Opret din bruger og kom i gang med at administrere dit revir.'
                  : 'Log ind for at få adgang til dine revirer og plejeplaner.'}
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                backgroundColor: '#141810',
                borderRadius: '8px',
                padding: '4px',
                marginBottom: 20,
                border: '1px solid #2b3623',
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setAuthMode('login');
                  setError('');
                  setInfoMessage('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  minHeight: 44,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  border: 'none',
                  background: authMode === 'login' ? '#2e3a26' : 'transparent',
                  color: authMode === 'login' ? '#f1f4dc' : '#8a977e',
                  fontWeight: authMode === 'login' ? 600 : 400,
                  fontSize: '0.88rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                Log ind
              </button>
              <button
                type="button"
                onClick={() => {
                  setAuthMode('signup');
                  setError('');
                  setInfoMessage('');
                }}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  minHeight: 44,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  border: 'none',
                  background: authMode === 'signup' ? '#2e3a26' : 'transparent',
                  color: authMode === 'signup' ? '#f1f4dc' : '#8a977e',
                  fontWeight: authMode === 'signup' ? 600 : 400,
                  fontSize: '0.88rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                Opret profil
              </button>
            </div>

            {error && (
              <div
                style={{
                  backgroundColor: 'rgba(217, 83, 79, 0.15)',
                  border: '1px solid #d9534f',
                  color: '#f8d7da',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  fontSize: '0.88rem',
                  marginBottom: 16,
                }}
              >
                {error}
              </div>
            )}

            {infoMessage && (
              <div
                style={{
                  backgroundColor: 'rgba(92, 184, 92, 0.15)',
                  border: '1px solid #5cb85c',
                  color: '#d4edda',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  fontSize: '0.88rem',
                  marginBottom: 16,
                }}
              >
                {infoMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="form-grid">
              {authMode === 'signup' && (
                <label className="field-label">
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    Navn
                  </span>
                  <input
                    data-autofocus
                    className="form-input"
                    type="text"
                    autoComplete="name"
                    placeholder="Dit fulde navn"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>
              )}

              <label className="field-label">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <Mail size={14} /> E-mail
                </span>
                <input
                  data-autofocus
                  className="form-input"
                  type="email"
                  autoComplete="username"
                  required
                  placeholder="din@email.dk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>

              <label className="field-label">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <Lock size={14} /> Adgangskode
                </span>
                <input
                  className="form-input"
                  type="password"
                  autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                  required
                  placeholder={authMode === 'signup' ? 'Mindst 6 tegn' : '••••••••••'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>

              {authMode === 'signup' && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    color: '#cbd5e1',
                    marginTop: '8px',
                    marginBottom: '4px',
                    lineHeight: '1.4',
                  }}
                >
                  <input
                    type="checkbox"
                    required
                    checked={acceptedTermsSignup}
                    onChange={(e) => setAcceptedTermsSignup(e.target.checked)}
                    style={{ marginTop: '2px', accentColor: '#16a34a', flexShrink: 0 }}
                  />
                  <span>
                    Jeg accepterer Plotwises{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setLegalDoc('terms');
                        setShowLegalModal(true);
                      }}
                      style={{
                        color: '#4ade80',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: 'inherit',
                      }}
                    >
                      brugerbetingelser & ansvarsafgrænsning
                    </button>{' '}
                    samt{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setLegalDoc('privacy');
                        setShowLegalModal(true);
                      }}
                      style={{
                        color: '#4ade80',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: 'inherit',
                      }}
                    >
                      privatlivspolitik
                    </button>
                    .
                  </span>
                </label>
              )}

              <button
                type="submit"
                className="button primary"
                disabled={busy}
                style={{ width: '100%', marginTop: 8, padding: '12px', minHeight: 44 }}
              >
                {busy
                  ? authMode === 'signup'
                    ? 'Opretter profil…'
                    : 'Logger ind…'
                  : authMode === 'signup'
                    ? 'Opret profil'
                    : 'Log ind'}
              </button>
              {authMode === 'login' && (
                <button
                  type="button"
                  className="button secondary"
                  disabled={busy}
                  style={{ width: '100%', minHeight: 44 }}
                  onClick={async () => {
                    setBusy(true);
                    setError('');
                    setInfoMessage('');
                    try {
                      await requestPasswordReset(email);
                      setInfoMessage('Hvis kontoen findes, modtager du en e-mail med loginhjælp.');
                    } catch (err) {
                      setError(
                        err instanceof Error ? err.message : 'Loginhjælp kunne ikke sendes.',
                      );
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Glemt adgangskode
                </button>
              )}
            </form>
          </div>
        </div>
      )}

      <section
        id="saadan"
        className="landing-workflow landing-reveal"
        aria-label="Sådan bruger du Plotwise"
      >
        <div className="landing-workflow-copy">
          <p className="landing-section-label">FRA IDÉ TIL VILDTAGER</p>
          <h2>
            En plan, der tager
            <br />
            form på din jord.
          </h2>
          <p className="landing-section-description">
            Fra den første streg på kortet til frøene i jorden. Plotwise samler det hele ét sted.
          </p>
          <div className="landing-workflow-steps">
            {workflow.map(({ title, text }, index) => (
              <div
                className={`landing-step ${activeStep === index ? 'is-active' : ''}`}
                key={title}
              >
                <button
                  onClick={() => setActiveStep(index)}
                  aria-expanded={activeStep === index}
                  aria-controls={`landing-step-${index}`}
                >
                  <span>0{index + 1}</span>
                  <strong>{title}</strong>
                  <ArrowRight size={19} />
                </button>
                <div id={`landing-step-${index}`} hidden={activeStep !== index}>
                  <p>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="landing-preview" aria-label="Forhåndsvisning af Plotwise">
          <div className="landing-preview-toolbar">
            <span>
              <Leaf size={16} /> Mit revir
            </span>
            <span>EKSEMPEL</span>
          </div>
          <div className="landing-preview-map">
            <img src={aerial} alt="Luftfoto som baggrund for en illustreret mark" loading="lazy" />
            <svg viewBox="0 0 500 330" aria-hidden="true">
              <path d="M106 117 287 60 391 143 321 263 147 245Z" />
            </svg>
            <span className="landing-preview-label">
              <MapPin size={14} /> Engstykket <strong>1,4 ha</strong>
            </span>
            <span className="landing-preview-compass">
              N<br />↑
            </span>
          </div>
          <div className="landing-preview-detail" aria-live="polite" key={activeStep}>
            <PreviewIcon size={22} />
            <div>
              <span>{workflow[activeStep].label}</span>
              <strong>
                {
                  [
                    'Et lille stykke jord. Mange muligheder.',
                    'Engstykket · Deer Explosion',
                    'Dit frøbehov, samlet ét sted.',
                  ][activeStep]
                }
              </strong>
              <p>
                {
                  [
                    'Tegn marken og lad Plotwise beregne arealet.',
                    'Frøblandinger og pleje følger den enkelte mark.',
                    'Fra dine marker til de rette poser i kurven.',
                  ][activeStep]
                }
              </p>
            </div>
          </div>
          <p className="landing-preview-caption">
            Et glimt af Plotwise. Udforsk mere i den interaktive rundtur.
          </p>
          <button
            type="button"
            className="landing-preview-tour-btn"
            onClick={() => {
              window.scrollTo({ top: 0 });
              handleStartTour();
            }}
          >
            <Compass size={16} />
            <span>Prøv den interaktive rundtur</span>
            <ArrowRight size={15} />
          </button>
        </div>
      </section>

      <section className="landing-invitation landing-reveal">
        <div>
          <p className="landing-section-label">DIT NÆSTE SKRIDT</p>
          <h2>
            Lad os få dine planer
            <br />
            ud i det fri.
          </h2>
          <p>Tag en rundtur og mærk, hvordan Plotwise passer til dit revir.</p>
        </div>
        <div className="landing-invitation-actions">
          <button
            className="landing-start"
            onClick={() => {
              window.scrollTo({ top: 0 });
              if (onStartGuest) {
                onStartGuest();
              } else {
                handleStartTour();
              }
            }}
          >
            Tegn din vildtager gratis <ArrowRight size={19} />
          </button>
          <p>
            Vil du have din egen profil?
            <br />
            <a href="mailto:plotwise.faunacare@gmail.com">Kontakt Emil Beiner</a>
          </p>
        </div>
      </section>
      <footer
        className="landing-footer"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '36px 16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          backgroundColor: '#0a120c',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            fontSize: '0.85rem',
            color: '#cbd5e1',
          }}
        >
          <a
            href="/"
            className="landing-footer-brand"
            style={{
              fontWeight: 700,
              color: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Leaf size={18} color="#4ade80" /> plotwise.
          </a>
          <span>·</span>
          <span>
            Leveret af <strong>EMService V/Emil Mortensen</strong>
          </span>
          <span>·</span>
          <span>
            CVR: <strong>44220695</strong>
          </span>
          <span>·</span>
          <span>Vognmandsvej 1, 4480 Store Fuglede</span>
          <span>·</span>
          <a
            href="mailto:Emilbeiner@gmail.com"
            style={{ color: '#4ade80', textDecoration: 'underline' }}
          >
            Emilbeiner@gmail.com
          </a>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            fontSize: '0.78rem',
            color: '#94a3b8',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginTop: '2px',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setLegalDoc('terms');
              setShowLegalModal(true);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              textDecoration: 'underline',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Brugerbetingelser & Ansvarsafgrænsning
          </button>
          <span>·</span>
          <button
            type="button"
            onClick={() => {
              setLegalDoc('privacy');
              setShowLegalModal(true);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              textDecoration: 'underline',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Privatlivspolitik (GDPR)
          </button>
          <span>·</span>
          <a
            href="https://shop.faunamaster.com/discount/BEINER"
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              void recordFaunamasterClick({
                source: 'shop_link',
                targetUrl: 'https://shop.faunamaster.com/discount/BEINER',
              });
            }}
            style={{ color: '#94a3b8', textDecoration: 'underline' }}
          >
            Frø købes hos Faunamaster ApS (5% rabatkode: BEINER)
          </a>
        </div>
      </footer>

      <CookieBanner
        onOpenPrivacy={() => {
          setLegalDoc('privacy');
          setShowLegalModal(true);
        }}
      />

      <LegalTermsModal
        initialDoc={legalDoc}
        isOpen={showLegalModal}
        onClose={() => setShowLegalModal(false)}
      />
    </main>
  );
}
